import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from './auth-context';

/**
 * The gate in front of the panel.
 *
 * No loading branch on purpose: `AuthProvider` does not render this until it
 * knows, so `status` here is never `unknown`.
 */
export function RequireAuth() {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status !== 'authed' || !user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // An account still carrying a password somebody else chose gets exactly one
  // screen until it picks its own. The backend refuses the rest anyway; this
  // is so the refusal is never what an operator sees.
  if (user.mustChangePassword && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace />;
  }

  return <Outlet />;
}

/**
 * Where to go after signing in.
 *
 * Only a path within this app. Anything that could be read as another origin —
 * `//evil.example`, `https://evil.example` — is dropped, because it arrives
 * from navigation state that a crafted link can set.
 */
export function safeNext(from: unknown): string {
  return typeof from === 'string' && /^\/(?!\/)/.test(from) ? from : '/';
}
